/**
 * SPDX-FileCopyrightText: (c) 2026 Liferay, Inc. https://liferay.com
 * SPDX-License-Identifier: LGPL-2.1-or-later OR LicenseRef-Liferay-DXP-EULA-2.0.0-2023-06
 */

import Icon from '@clayui/icon';
import {LinkOrButton} from '@clayui/shared';
import classNames from 'classnames';
import React from 'react';

export interface IPropsLink
	extends React.AnchorHTMLAttributes<HTMLAnchorElement> {

	/**
	 * Flag to indicate if `active` class should be applied.
	 */
	active?: boolean;

	/**
	 * Flag to indicate if `collapsed` class should be applied.
	 */
	collapsed?: boolean;

	/**
	 * Flag to indicate if `disabled` class should be applied.
	 */
	disabled?: boolean;

	/**
	 * Flag to indicate if icon should be shown.
	 */
	showIcon?: boolean;

	/**
	 * Path to the spritemap that Icon should use when referencing symbols.
	 */
	spritemap?: string;
}

const Link = React.forwardRef<
	HTMLButtonElement | HTMLAnchorElement | HTMLDivElement,
	IPropsLink
>(
	(
		{
			active,
			children,
			className,
			collapsed,
			disabled,
			showIcon,
			spritemap,
			...otherProps
		},
		ref
	) => {
		return (
			<LinkOrButton
				{...otherProps}
				buttonDisplayType="unstyled"
				buttonType="button"
				className={classNames('nav-link', className, {
					active,
					['collapse-icon']: showIcon,
					collapsed,
					disabled,
				})}
				ref={ref}
			>
				{children}

				{showIcon && (
					<>
						<span className="collapse-icon-closed">
							<Icon
								focusable="false"
								role="presentation"
								spritemap={spritemap}
								symbol="angle-right-small"
							/>
						</span>

						<span className="collapse-icon-open">
							<Icon
								focusable="false"
								role="presentation"
								spritemap={spritemap}
								symbol="angle-down-small"
							/>
						</span>
					</>
				)}
			</LinkOrButton>
		);
	}
);

Link.displayName = 'NavLink';

export {Link};
